import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { compareKeys } from "../../forms/tools";
import {
  GetFileDownloadUrlCommandInput,
  ListFilesCommandInput,
  ClaimFile,
} from "@amzn/ttechclaimintakeservice-client";
import { listFilesService } from "src/api/files/listFiles";
import { getFileService } from "src/api/files/getFileDownloadUrl";
import { deleteFileService } from "src/api/files/deleteFile";

import {
  AUTHORIZATION_FORM_DOCUMENT_TYPE,
  MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE,
  MEDICAL_PANEL_DOCUMENT_TYPE,
  STATE_FORM_DOCUMENT_TYPE,
} from "src/views/workersCompensationDocuments/constants";
import { uploadFileService } from "src/api/files/uploadFile";

interface DocumentTypeState {
  [index: string]: any;
  files: ClaimFile[];
  fileUrl: string;
  filesRetrieved: boolean;
  gettingFiles: boolean;
  fileUrlRetrieved: boolean;
  gettingFileUrl: boolean;
  deletingFile: boolean;
  deleteFileSuccess: boolean;
  uploadingFiles: boolean;
  uploadFilesSuccess: boolean;
  listFilesError: string;
  getFileUrlError: string;
  deleteFileError: string;
  uploadFilesError: string;
}
const initialDocumentTypeState: DocumentTypeState = {
  files: [],
  fileUrl: "",
  filesRetrieved: false,
  gettingFiles: false,
  fileUrlRetrieved: false,
  gettingFileUrl: false,
  deletingFile: false,
  deleteFileSuccess: false,
  listFilesError: "",
  getFileUrlError: "",
  deleteFileError: "",
  uploadingFiles: false,
  uploadFilesSuccess: false,
  uploadFilesError: "",
};
// Define a type for the slice state
export interface WorkersCompensationDocumentsState {
  [index: string]: DocumentTypeState;
  [MEDICAL_PANEL_DOCUMENT_TYPE]: DocumentTypeState;
  [AUTHORIZATION_FORM_DOCUMENT_TYPE]: DocumentTypeState;
  [STATE_FORM_DOCUMENT_TYPE]: DocumentTypeState;
  [MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE]: DocumentTypeState;
}
export const initialState: WorkersCompensationDocumentsState = {
  [MEDICAL_PANEL_DOCUMENT_TYPE]: initialDocumentTypeState,
  [AUTHORIZATION_FORM_DOCUMENT_TYPE]: initialDocumentTypeState,
  [STATE_FORM_DOCUMENT_TYPE]: initialDocumentTypeState,
  [MULTIPLE_ITEM_CLAIM_ATTACHMENT_TEMPLATE]: initialDocumentTypeState,
};

export const verifyStateIsValid = (
  state: WorkersCompensationDocumentsState
) => {
  return compareKeys(state, initialState);
};
/*eslint-disable @typescript-eslint/no-unused-vars*/
export const listFiles = createAsyncThunk(
  "workersCompensationDocuments/listFiles",
  async (input: ListFilesCommandInput, thunkAPI: any) => {
    const response = await listFilesService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const getFile = createAsyncThunk(
  "workersCompensationDocuments/getFile",
  async (input: GetFileDownloadUrlCommandInput, thunkAPI: any) => {
    const response = await getFileService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const deleteFile = createAsyncThunk(
  "workersCompensationDocuments/deleteFile",
  async (input: GetFileDownloadUrlCommandInput, thunkAPI: any) => {
    const response = await deleteFileService(input);
    return { ...response, fileType: input.fileType };
  }
);

export const uploadMultipleFiles = createAsyncThunk(
  "workersCompensationDocuments/uploadMultipleFiles",
  async (
    { files, fileType }: { files: File[]; fileType: string },
    thunkAPI: any
  ) => {
    const responses = [];
    for (const file of files) {
      try {
        const response = await uploadFileService(
          {
            key: file.name,
            contentLength: file.size,
            fileType,
          },
          file
        );
        responses.push(response);
      } catch (error) {
        return error;
      }
    }
    return { responses, fileType };
  }
);

/*eslint-enable @typescript-eslint/no-unused-vars*/

export const workersCompensationDocumentsSlice = createSlice({
  name: "workersCompensationDocuments",
  initialState,
  reducers: {
    resetWorkersCompensationDocumentsState: () => {
      return initialState;
    },
    setNewWorkersCompensationDocumentsState: (
      state: WorkersCompensationDocumentsState,
      action: PayloadAction<{
        newState: DocumentTypeState;
        fileType: string;
      }>
    ) => {
      const { newState, fileType } = action.payload;

      return { ...state, [fileType]: newState };
    },
    setValueInWorkersCompensationDocumentsState: (
      state: WorkersCompensationDocumentsState,
      action: PayloadAction<{
        key: string;
        value: string | boolean | File[];
        fileType: string;
      }>
    ) => {
      const { fileType, key, value } = action.payload;
      state[fileType][key] = value;
    },
  },
  extraReducers: (builder: any) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(
        listFiles.fulfilled,
        (
          state: WorkersCompensationDocumentsState,
          action: PayloadAction<any>
        ) => {
          const { files, fileType } = action.payload;
          state[fileType].files = files;
          state[fileType].gettingFiles = false;
          state[fileType].filesRetrieved = true;
        }
      )
      .addCase(
        listFiles.pending,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;
          state[fileType].gettingFiles = true;
          state[fileType].listFilesError = "";
        }
      )
      .addCase(
        listFiles.rejected,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;
          state[fileType].gettingFiles = false;
          state[fileType].filesRetrieved = false;
          state.listFilesError = action.error.message;
        }
      )

      .addCase(
        getFile.fulfilled,
        (
          state: WorkersCompensationDocumentsState,
          action: PayloadAction<any>
        ) => {
          const { fileType, downloadUrl } = action.payload;
          state[fileType].fileUrl = downloadUrl;
          state[fileType].fileUrlRetrieved = true;
          state[fileType].gettingFileUrl = false;
          state[fileType].getFileUrlError = "";
        }
      )
      .addCase(
        getFile.pending,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;
          state[fileType].gettingFileUrl = true;
          state[fileType].getFileUrlError = "";
        }
      )
      .addCase(
        getFile.rejected,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;

          state[fileType].fileUrlRetrieved = false;
          state[fileType].gettingFileUrl = false;
          state.getFileUrlError = action.error.message;
        }
      )

      .addCase(
        deleteFile.fulfilled,
        (
          state: WorkersCompensationDocumentsState,
          action: PayloadAction<any>
        ) => {
          const { fileType } = action.payload;
          state[fileType].deleteFileSuccess = true;
          state[fileType].deletingFile = false;
          state[fileType].deleteFileError = "";
        }
      )
      .addCase(
        deleteFile.pending,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;
          state[fileType].deleteFileSuccess = false;
          state[fileType].deletingFile = true;
          state[fileType].deleteFileError = "";
        }
      )
      .addCase(
        deleteFile.rejected,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;

          state[fileType].deleteFileSuccess = false;
          state[fileType].deletingFile = false;
          state[fileType].deleteFileError = action.error.message;
        }
      )
      .addCase(
        uploadMultipleFiles.fulfilled,
        (
          state: WorkersCompensationDocumentsState,
          action: PayloadAction<any>
        ) => {
          const { fileType } = action.payload;
          state[fileType].uploadFilesSuccess = true;
          state[fileType].uploadingFiles = false;
          state[fileType].uploadFilesError = "";
        }
      )
      .addCase(
        uploadMultipleFiles.pending,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;
          state[fileType].uploadFilesSuccess = false;
          state[fileType].uploadingFiles = true;
          state[fileType].uploadFilesError = "";
        }
      )
      .addCase(
        uploadMultipleFiles.rejected,
        (state: WorkersCompensationDocumentsState, action: any) => {
          const { fileType } = action.meta.arg;

          state[fileType].uploadFilesSuccess = false;
          state[fileType].uploadingFiles = false;
          state[fileType].uploadFilesError = action.error.message;
        }
      );
  },
});

export const {
  resetWorkersCompensationDocumentsState,
  setValueInWorkersCompensationDocumentsState,
  setNewWorkersCompensationDocumentsState,
} = workersCompensationDocumentsSlice.actions;

export default workersCompensationDocumentsSlice.reducer;
